import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  TextField,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  Tooltip,
  IconButton
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  FilterList as FilterIcon,
  Download as DownloadIcon,
  Security as SecurityIcon
} from '@mui/icons-material';
import { format } from 'date-fns';

const UserAuditLog = () => {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  
  // Filters
  const [filters, setFilters] = useState({
    username: '',
    action: '',
    resource_type: '',
    success: '',
    date_from: '',
    date_to: ''
  });

  const fetchAuditLogs = async (currentPage = page) => {
    setLoading(true);
    setError('');
    
    try {
      const sessionToken = localStorage.getItem('sessionToken');
      if (!sessionToken) {
        setError('Authentication required');
        setLoading(false);
        return;
      }

      const params = new URLSearchParams({
        page: currentPage + 1,
        limit: rowsPerPage,
        ...Object.fromEntries(Object.entries(filters).filter(([_, v]) => v !== ''))
      });

      const response = await fetch(`/api/audit/logs?${params}`, {
        headers: {
          'Authorization': `Bearer ${sessionToken}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setLogs(data.data);
        setTotalCount(data.pagination.total);
      } else {
        setError(data.error || 'Failed to fetch audit logs');
      }
    } catch (err) {
      console.error('Error fetching audit logs:', err);
      setError('Failed to fetch audit logs');
    } finally {
      setLoading(false);
    }
  };

  const fetchAuditStats = async () => {
    try {
      const sessionToken = localStorage.getItem('sessionToken');
      if (!sessionToken) return;

      const response = await fetch('/api/audit/stats', {
        headers: {
          'Authorization': `Bearer ${sessionToken}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      if (response.ok && data.success) {
        setStats(data.data);
      }
    } catch (err) {
      console.error('Error fetching audit stats:', err);
    }
  };

  useEffect(() => {
    fetchAuditLogs();
    fetchAuditStats();
  }, [page, rowsPerPage]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const applyFilters = () => {
    setPage(0);
    fetchAuditLogs(0);
  };

  const clearFilters = () => {
    setFilters({
      username: '',
      action: '',
      resource_type: '',
      success: '',
      date_from: '',
      date_to: ''
    });
    setPage(0);
    setTimeout(() => fetchAuditLogs(0), 100);
  };

  const getStatusChip = (success) => {
    return (
      <Chip
        label={success ? 'Success' : 'Failed'}
        color={success ? 'success' : 'error'}
        size="small"
        variant="outlined"
      />
    );
  };

  const getResourceTypeChip = (resourceType) => {
    if (!resourceType) return null;
    
    const colors = {
      'returns': 'primary',
      'amazon_returns': 'secondary',
      'users': 'info',
      'auth': 'warning'
    };

    return (
      <Chip
        label={resourceType}
        color={colors[resourceType] || 'default'}
        size="small"
        variant="filled"
      />
    );
  };

  if (loading && logs.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <SecurityIcon color="primary" />
        <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
          User Audit Log
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Track all user activities and system events
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Statistics Cards */}
      {stats && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Total Actions (30d)
                </Typography>
                <Typography variant="h4" component="div">
                  {stats.totalActions || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Active Users (30d)
                </Typography>
                <Typography variant="h4" component="div">
                  {stats.activeUsers || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Failed Actions (30d)
                </Typography>
                <Typography variant="h4" component="div" color="error">
                  {stats.failedActions || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Success Rate (30d)
                </Typography>
                <Typography variant="h4" component="div" color="success.main">
                  {stats.totalActions > 0 
                    ? `${Math.round(((stats.totalActions - stats.failedActions) / stats.totalActions) * 100)}%`
                    : '100%'
                  }
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="end">
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              label="Username"
              value={filters.username}
              onChange={(e) => handleFilterChange('username', e.target.value)}
              size="small"
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              label="Action"
              value={filters.action}
              onChange={(e) => handleFilterChange('action', e.target.value)}
              size="small"
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl size="small" fullWidth>
              <InputLabel>Resource Type</InputLabel>
              <Select
                value={filters.resource_type}
                onChange={(e) => handleFilterChange('resource_type', e.target.value)}
                label="Resource Type"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="returns">Returns</MenuItem>
                <MenuItem value="amazon_returns">Amazon Returns</MenuItem>
                <MenuItem value="users">Users</MenuItem>
                <MenuItem value="auth">Authentication</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl size="small" fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={filters.success}
                onChange={(e) => handleFilterChange('success', e.target.value)}
                label="Status"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="true">Success</MenuItem>
                <MenuItem value="false">Failed</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              label="From Date"
              type="date"
              value={filters.date_from}
              onChange={(e) => handleFilterChange('date_from', e.target.value)}
              size="small"
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              label="To Date"
              type="date"
              value={filters.date_to}
              onChange={(e) => handleFilterChange('date_to', e.target.value)}
              size="small"
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} md={12}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button variant="contained" onClick={applyFilters} startIcon={<FilterIcon />}>
                Apply Filters
              </Button>
              <Button variant="outlined" onClick={clearFilters}>
                Clear
              </Button>
              <Button variant="outlined" onClick={() => fetchAuditLogs()} startIcon={<RefreshIcon />}>
                Refresh
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Audit Log Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Date/Time</TableCell>
                <TableCell>User</TableCell>
                <TableCell>Action</TableCell>
                <TableCell>Resource</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>IP Address</TableCell>
                <TableCell>Details</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {logs.map((log) => (
                <TableRow key={log.id} hover>
                  <TableCell>
                    <Typography variant="body2">
                      {format(new Date(log.created_at), 'MMM dd, yyyy HH:mm:ss')}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" fontWeight="medium">
                        {log.username}
                      </Typography>
                      {log.user_role && (
                        <Chip label={log.user_role} size="small" color="info" variant="outlined" />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium" color="primary">
                      {log.action}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      {getResourceTypeChip(log.resource_type)}
                      {log.resource_id && (
                        <Typography variant="caption" color="text.secondary">
                          ID: {log.resource_id}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    {getStatusChip(log.success)}
                    {log.error_message && (
                      <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                        {log.error_message}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {log.ip_address || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {log.details ? (
                      <Tooltip title={JSON.stringify(log.details, null, 2)}>
                        <Typography variant="body2" color="primary" sx={{ cursor: 'pointer' }}>
                          View Details
                        </Typography>
                      </Tooltip>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        -
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {logs.length === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                      No audit logs found
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
    </Box>
  );
};

export default UserAuditLog;